import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import Footer from "@/components/Footer";
import WorkspaceSwitcher from "@/components/WorkspaceSwitcher";
import LaneChrome from "@/components/LaneChrome";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCompanyBranding } from "@/hooks/useCompanyBranding";
import InstallPrompt from "@/components/InstallPrompt";
import AlertsBell from "@/components/AlertsBell";
import FounderBadge from "@/components/FounderBadge";
import { CompanyTzProvider } from "@/context/CompanyTzContext";
import { LayoutDashboard, Users, PlayCircle, FileText, Building2, BarChart3, LogOut, User, Landmark, MapPin, Clock, ClipboardCheck, Shield, Sparkles, CreditCard, CalendarClock, CalendarDays, CalendarRange, Archive, Zap } from "lucide-react";

const links = [
  { to: "/app", end: true, label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/app/employees", label: "Employees", icon: Users, testid: "nav-employees" },
  { to: "/app/worksites", label: "Worksites", icon: MapPin, testid: "nav-worksites" },
  { to: "/app/timesheets", label: "Timesheets", icon: CalendarDays, testid: "nav-timesheets" },
  { to: "/app/approvals", label: "Approvals", icon: ClipboardCheck, testid: "nav-approvals" },
  { to: "/app/payroll", label: "Payroll Runs", icon: PlayCircle, testid: "nav-payroll" },
  { to: "/app/payroll-calendar", label: "Payroll Calendar", icon: CalendarRange, testid: "nav-payroll-calendar" },
  { to: "/app/payments", label: "Payments", icon: Landmark, testid: "nav-payments" },
  { to: "/app/paystubs", label: "Pay Stubs", icon: FileText, testid: "nav-paystubs" },
  { to: "/app/company", label: "Company", icon: Building2, testid: "nav-company" },
  { to: "/app/workflow-settings", label: "AI Office Manager", icon: Zap, testid: "nav-workflow-settings" },
  { to: "/app/reports", label: "Reports", icon: BarChart3, testid: "nav-reports" },
  { to: "/app/tax-tables", label: "Tax Tables", icon: Landmark, testid: "nav-tax-tables" },
  { to: "/app/year-end", label: "Year-End Prep", icon: Archive, testid: "nav-year-end" },
  { to: "/app/resources", label: "Resources", icon: Sparkles, testid: "nav-resources" },
  { to: "/app/audit", label: "Audit Log", icon: Shield, testid: "nav-audit" },
  { to: "/app/billing", label: "Billing", icon: CreditCard, testid: "nav-billing" },
];

const employeeLinks = [
  { to: "/app/portal", end: true, label: "My Portal", icon: User, testid: "nav-portal" },
  { to: "/app/timesheets", label: "My Timesheets", icon: CalendarDays, testid: "nav-timesheets" },
  { to: "/app/payroll-calendar", label: "Pay Calendar", icon: CalendarRange, testid: "nav-payroll-calendar" },
  { to: "/app/clock", label: "Quick Clock In/Out", icon: Clock, testid: "nav-clock" },
  { to: "/app/time-off", label: "Time Off", icon: CalendarClock, testid: "nav-time-off" },
  { to: "/app/paystubs", label: "My Pay Stubs", icon: FileText, testid: "nav-paystubs" },
  { to: "/app/resources", label: "Resources", icon: Sparkles, testid: "nav-resources" },
];

export default function DashboardLayout() {  const { user, logout } = useAuth();
  const nav = useNavigate();
  const { logo, name } = useCompanyBranding(user);
  const [proActive, setProActive] = useState(true);  // optimistic: don't flash-hide
  const [mobileNav, setMobileNav] = useState(false);

  // Pro-tier gating for sidebar links. Admins on a non-Pro plan should not
  // even see Worksites / AI Office Manager / Reports (audit requirement).
  // Employees always see their own trimmed list.
  useEffect(() => {
    if (user?.role !== "admin") { setProActive(true); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/billing/status");
        if (!cancelled) setProActive(!!data?.pro?.active);
      } catch {
        if (!cancelled) setProActive(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  // Reports stays visible for non-Pro admins — they see a locked sneak-peek
  // page instead of a hard bounce. Everything else is fully hidden.
  const PRO_ONLY = new Set(["nav-worksites", "nav-workflow-settings", "nav-year-end", "nav-payroll-calendar"]);
  const rawItems = user?.role === "employee" ? employeeLinks : links;
  const items = proActive ? rawItems : rawItems.filter((l) => !PRO_ONLY.has(l.testid));

  const handleLogout = async () => {
    await logout();
    nav("/login");
  };

  return (
    <div className="min-h-screen bg-paper flex">
      <InstallPrompt />
      {mobileNav && (
        <button
          aria-label="Close navigation"
          data-testid="nav-backdrop"
          onClick={() => setMobileNav(false)}
          className="fixed inset-0 z-30 bg-ink/50 md:hidden"
        />
      )}
      <aside className={`w-64 bg-white border-r-2 border-ink flex flex-col shrink-0 sticky top-0 h-screen z-40 max-md:fixed max-md:transition-transform ${mobileNav ? "max-md:translate-x-0" : "max-md:-translate-x-full"}`} data-testid="dashboard-sidebar">
        <div className="px-6 h-16 border-b-2 border-ink flex items-center gap-3">
          {logo ? (
            <>
              <div className="w-8 h-8 flex items-center justify-center overflow-hidden shrink-0">
                <img src={logo} alt="logo" data-testid="brand-logo" className="max-w-full max-h-full object-contain" />
              </div>
              <div>
                <div className="font-heading font-black tracking-tight leading-none truncate max-w-[9rem]" data-testid="brand-name">{name}</div>
                <div className="overline text-[10px] text-muted-foreground">PAYROLL CONSOLE</div>
              </div>
            </>
          ) : (
            // No company logo uploaded: show the Checkmate wordmark at a legible
            // width rather than crushing it into a square tile.
            <div className="min-w-0">
              <img src="/checkmate-logo.png" alt="Checkmate Payroll" data-testid="brand-logo-fallback" className="h-6 w-auto" />
              <div className="overline text-[10px] text-muted-foreground truncate max-w-[11rem] mt-1" data-testid="brand-name">{name}</div>
            </div>
          )}
        </div>
        <nav className="flex-1 py-6">
          {items.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={l.testid}
              onClick={() => setMobileNav(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-6 py-3 font-mono text-sm uppercase tracking-widest border-l-4 ${
                  isActive ? "bg-ink text-white border-ink" : "border-transparent hover:bg-secondary text-ink"
                }`
              }
            >
              <l.icon className="w-4 h-4" strokeWidth={2.25} />
              {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t-2 border-ink p-4 space-y-3">
          <div className="text-xs">
            <div className="flex items-center gap-2 min-w-0">
              <div className="font-heading font-black truncate" data-testid="current-user-name">{user?.name}</div>
              <FounderBadge user={user} size="sm" withLabel={false} />
            </div>
            <div className="font-mono text-muted-foreground truncate">{user?.email}</div>
            <div className="overline mt-1">{user?.is_root_admin ? "root admin · directorate" : user?.role}</div>
          </div>
          <button onClick={handleLogout} data-testid="logout-btn" className="w-full flex items-center justify-center gap-2 border-2 border-ink py-2 font-mono text-xs uppercase tracking-widest hover:bg-ink hover:text-white transition-colors">
            <LogOut className="w-4 h-4" /> Sign Out
          </button>
        </div>
      </aside>
      <main className="flex-1 min-w-0">
        <div className="sticky top-0 z-30 border-b-2 border-ink bg-paper">
          <div className="h-16 px-6 flex items-center justify-end gap-3">
            <FounderBadge user={user} />
            <button
              onClick={() => setMobileNav(true)}
              data-testid="open-mobile-nav"
              aria-label="Open navigation"
              className="md:hidden mr-auto border-2 border-ink px-3 py-1.5 font-mono uppercase text-[10px] tracking-widest"
            >
              ☰ Menu
            </button>
            <WorkspaceSwitcher />
            <AlertsBell role={user?.role} />
          </div>
          <LaneChrome />
        </div>
        {/* No z-index here: a positioned wrapper would become its own
            stacking context and every `fixed inset-0 z-50` modal rendered
            inside the Outlet would lose to the z-40 sidebar and come out
            sliced down its left edge. */}
        <div className="relative">
          <CompanyTzProvider>
            <Outlet />
          </CompanyTzProvider>
          <Footer />
        </div>
      </main>
    </div>
  );
}
